import { Textarea as HeadlessTextarea } from '@headlessui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import type { DetailedHTMLProps, ForwardedRef, MouseEvent, ReactNode, TextareaHTMLAttributes } from 'react';
import { useId, forwardRef } from 'react';
import Button from '@/components/common/Button';
import type { FieldProps } from '@/components/common/Field';
import Field from '@/components/common/Field';
import InputGroup from '@/components/common/Input/InputGroup';

export type TextareaProps = DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> &
    Omit<FieldProps, 'id' | 'children'> & {
        inputState?: 'valid' | 'invalid' | 'normal';
        prefixElement?: ReactNode;
        suffixElement?: ReactNode;
        onSuffixClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    };

const TextareaWithRef = (
    {
        id,
        label,
        labelExtra,
        description,
        className,
        helpElement,
        inputState = 'normal',
        errorElement,
        prefixElement,
        suffixElement: suffixElementFromProps,
        onSuffixClick,
        labelSize,
        labelWeight,
        ...props
    }: TextareaProps,
    ref: ForwardedRef<HTMLTextAreaElement>
) => {
    const elementId = useId();
    const computedId = id || elementId;
    const inputTextSize = 'text-base';
    const inputLeading = 'leading-6';
    const inputFontWeight = 'font-medium';

    let suffixElement = suffixElementFromProps;

    if (inputState === 'invalid') {
        suffixElement = <ExclamationCircleIcon aria-hidden="true" className="size-5 text-red-500" />;
    } else if (inputState === 'valid') {
        suffixElement = <CheckCircleIcon aria-hidden="true" className="size-5 text-green-500" />;
    }

    const input = (
        <InputGroup disabled={props.disabled} inputState={inputState}>
            {prefixElement && (
                <div className="text-muted-foreground shrink-0 text-base select-none sm:text-sm/6">{prefixElement}</div>
            )}
            <HeadlessTextarea
                ref={ref}
                data-with-prefix={!!prefixElement || undefined}
                data-with-suffix={!!suffixElement || undefined}
                className={clsx(
                    // base
                    'placeholder:text-muted-foreground w-full grow resize-none border-0 bg-transparent px-0 py-1.5 text-neutral-800 shadow-none',
                    inputTextSize,
                    inputLeading,
                    inputFontWeight,
                    // focus
                    'focus:ring-0',
                    // margins for prefix and suffix
                    'data-with-prefix:pl-1 data-with-suffix:pr-1',
                    // disable state
                    'disabled:text-muted-foreground disabled:cursor-not-allowed disabled:bg-neutral-50'
                )}
                rows={2}
                id={computedId}
                {...props}
            />
            {suffixElement &&
                (onSuffixClick ? (
                    <Button
                        type="button"
                        className="text-muted-foreground inset-y-0 flex shrink-0 cursor-pointer items-center px-0 shadow-inherit hover:text-neutral-900"
                        onClick={onSuffixClick}
                    >
                        {suffixElement}
                    </Button>
                ) : (
                    <div className="text-muted-foreground shrink-0 text-base select-none sm:text-sm/6">
                        {suffixElement}
                    </div>
                ))}
        </InputGroup>
    );

    return (
        <Field
            name={props.name}
            id={computedId}
            label={label}
            description={description}
            errorElement={errorElement}
            helpElement={helpElement}
            labelExtra={labelExtra}
            required={props.required}
            labelSize={labelSize}
            labelWeight={labelWeight}
        >
            {input}
        </Field>
    );
};

const Textarea = forwardRef(TextareaWithRef);

export default Textarea;
